import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Row,
  Spinner,
} from 'react-bootstrap';
import { PencilSquare } from 'react-bootstrap-icons';
import { AppContext } from '../../App';
import AdHocLinesEditor from '../../components/Project/AdHocLinesEditor';
import CatalogQuoteEmailResultSummary from '../../components/Quote/CatalogQuoteEmailResultSummary';
import LabOneTimeContactFields from '../../components/Quote/LabOneTimeContactFields';
import GlobalOrderContactModal from '../../components/OrderRequest/GlobalOrderContactModal';
import GlassWorkspaceShell from '../../components/Glass/GlassWorkspaceShell';
import { getOrderContactSettings } from '../../clients/order_request_client';
import { getSupplierSelectList } from '../../clients/supplier_client';
import { requestAdHocQuoteEmails } from '../../clients/quote_client';
import { showToast } from '../../config_and_helpers/helpers';
import { logInfo } from '../../config_and_helpers/logger';
import { catalogEmailSuccessMessage } from '../../utils/catalogQuoteEmailResponse';
import { emptyAdHocFormRow } from '../../utils/projectAdHocPayload';
import {
  LAB_CONTACT_MODE,
  buildStandaloneAdHocQuoteEmailLines,
  buildStandalonePreviewLinesFromForm,
  buildStandaloneQuoteRequestContactPayload,
  canSendStandaloneQuote,
  defaultLabContactMode,
  emptyOneTimeLabContact,
  groupStandaloneLinesBySupplier,
  validateOneTimeLabContact,
  validateStandaloneAdHocQuoteLines,
} from '../../utils/standaloneAdHocQuoteRequest';

const LOG_CONTEXT = 'CustomQuoteRequestPage';

/**
 * Standalone custom-line quote emails (no project).
 */
const CustomQuoteRequestPage = () => {
  const { token } = useContext(AppContext);
  const [lines, setLines] = useState([emptyAdHocFormRow()]);
  const [supplierSelectList, setSupplierSelectList] = useState([]);
  const [isLoadingSuppliers, setIsLoadingSuppliers] = useState(false);
  const [globalContactSettings, setGlobalContactSettings] = useState(null);
  const [isLoadingGlobalContact, setIsLoadingGlobalContact] = useState(false);
  const [globalContactLoadError, setGlobalContactLoadError] = useState('');
  const [showGlobalContactModal, setShowGlobalContactModal] = useState(false);
  const [labContactMode, setLabContactMode] = useState(LAB_CONTACT_MODE.SAVED);
  const [oneTimeLabContact, setOneTimeLabContact] = useState(emptyOneTimeLabContact());
  const [labContactFieldErrors, setLabContactFieldErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitErrors, setSubmitErrors] = useState([]);
  const [successResult, setSuccessResult] = useState(null);

  const previewLines = useMemo(
    () => buildStandalonePreviewLinesFromForm(lines),
    [lines]
  );
  const supplierGroups = useMemo(
    () => groupStandaloneLinesBySupplier(previewLines),
    [previewLines]
  );

  const hasGlobalContact =
    !!globalContactSettings?.contact_name && !!globalContactSettings?.contact_email;

  const { canSend, disabledReason: sendDisabledReason } = useMemo(
    () =>
      canSendStandaloneQuote({
        mode: labContactMode,
        hasGlobalContact,
        isLoadingGlobalContact,
        globalContactLoadError,
        oneTimeContact: oneTimeLabContact,
      }),
    [
      labContactMode,
      hasGlobalContact,
      isLoadingGlobalContact,
      globalContactLoadError,
      oneTimeLabContact,
    ]
  );

  const loadContactSettings = useCallback(async () => {
    if (!token) return;
    setIsLoadingGlobalContact(true);
    setGlobalContactLoadError('');
    const response = await getOrderContactSettings(token);
    setIsLoadingGlobalContact(false);
    if (response?.success) {
      setGlobalContactSettings(response.data);
      setLabContactMode(defaultLabContactMode(
        !!(response.data?.contact_name && response.data?.contact_email)
      ));
    } else {
      setGlobalContactSettings(null);
      setGlobalContactLoadError(
        response?.messages?.[0] || 'Lab contact settings could not be loaded.'
      );
      setLabContactMode(LAB_CONTACT_MODE.ONE_TIME);
    }
  }, [token]);

  useEffect(() => {
    loadContactSettings();
  }, [loadContactSettings]);

  useEffect(() => {
    if (!token) return;
    setIsLoadingSuppliers(true);
    getSupplierSelectList(token, setSupplierSelectList).finally(() =>
      setIsLoadingSuppliers(false)
    );
  }, [token]);

  const handleLabContactModeChange = mode => {
    setLabContactMode(mode);
    setLabContactFieldErrors({});
    setSubmitErrors([]);
  };

  const handleOneTimeLabContactChange = patch => {
    setOneTimeLabContact(prev => ({ ...prev, ...patch }));
    setLabContactFieldErrors({});
  };

  const handleSend = async () => {
    setSubmitErrors([]);
    setLabContactFieldErrors({});
    setSuccessResult(null);

    const apiLines = buildStandaloneAdHocQuoteEmailLines(lines);
    const lineValidation = validateStandaloneAdHocQuoteLines(apiLines);
    if (!lineValidation.valid) {
      setSubmitErrors([lineValidation.message || 'Please fix the custom lines.']);
      return;
    }

    if (labContactMode === LAB_CONTACT_MODE.ONE_TIME) {
      const contactValidation = validateOneTimeLabContact(oneTimeLabContact);
      if (!contactValidation.valid) {
        setSubmitErrors([
          contactValidation.message || 'Please enter a valid one-time lab contact.',
        ]);
        if (contactValidation.fieldErrors) {
          setLabContactFieldErrors(contactValidation.fieldErrors);
        }
        return;
      }
    } else if (!canSend || !token) {
      setSubmitErrors([sendDisabledReason || 'Cannot send quote requests.']);
      return;
    }

    if (!token) {
      setSubmitErrors(['Cannot send quote requests.']);
      return;
    }

    const contactPayload = buildStandaloneQuoteRequestContactPayload(
      labContactMode,
      oneTimeLabContact
    );
    const hasOneTimeLabContact = labContactMode === LAB_CONTACT_MODE.ONE_TIME;

    setIsSubmitting(true);
    logInfo(LOG_CONTEXT, 'standalone adHoc quote submit', {
      lineCount: apiLines.length,
      supplierGroupCount: supplierGroups.length,
      labContactMode,
      hasOneTimeLabContact,
    });

    const response = await requestAdHocQuoteEmails(token, {
      lines: apiLines,
      ...contactPayload,
    });
    setIsSubmitting(false);

    if (response?.success) {
      setSuccessResult(response.data);
      showToast(
        catalogEmailSuccessMessage(response.data),
        'success',
        'top-right',
        5000
      );
      logInfo(LOG_CONTEXT, 'standalone adHoc quote success', {
        supplierCount: response.data?.suppliers?.length ?? 0,
      });
      return;
    }

    setSubmitErrors(
      response?.messages?.length
        ? response.messages
        : ['Request failed. Please try again.']
    );
  };

  const handleClearAfterSuccess = () => {
    setLines([emptyAdHocFormRow()]);
    setSuccessResult(null);
    setSubmitErrors([]);
    setLabContactFieldErrors({});
    setOneTimeLabContact(emptyOneTimeLabContact());
    setLabContactMode(defaultLabContactMode(hasGlobalContact));
  };

  const showLabContactWarning =
    !isLoadingGlobalContact &&
    !canSend &&
    sendDisabledReason &&
    labContactMode === LAB_CONTACT_MODE.SAVED;

  return (
    <GlassWorkspaceShell theme="yellow" wide>
    <div className="glass-panel">
      <Row className="mb-4">
        <Col>
          <h2 className="mb-2 glass-page-heading">Custom Quote Request Email</h2>
          <p className="glass-page-sub mb-0">
            Send supplier quote emails for custom lines without adding them to a
            project. For catalog inventory products, use the Shop cart or a
            project&apos;s catalog quote action.
          </p>
        </Col>
        <Col xs="auto" className="d-flex align-items-start">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setShowGlobalContactModal(true)}
          >
            {hasGlobalContact ? 'Lab contact set' : 'Set lab contact'}
          </Button>
        </Col>
      </Row>

      <Card className="mb-4">
        <Card.Header>Lab contact for this send</Card.Header>
        <Card.Body>
          {isLoadingGlobalContact ? (
            <div className="d-flex align-items-center text-muted small">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading lab contact settings…
            </div>
          ) : globalContactLoadError ? (
            <Alert variant="warning" className="mb-3 py-2">
              {globalContactLoadError}
            </Alert>
          ) : null}
          <LabOneTimeContactFields
            mode={labContactMode}
            onModeChange={handleLabContactModeChange}
            contactName={oneTimeLabContact.contactName}
            contactEmail={oneTimeLabContact.contactEmail}
            onContactChange={handleOneTimeLabContactChange}
            globalContactSettings={globalContactSettings}
            disabled={isSubmitting || isLoadingGlobalContact}
            fieldErrors={labContactFieldErrors}
            onConfigureGlobal={() => setShowGlobalContactModal(true)}
          />
        </Card.Body>
      </Card>

      {showLabContactWarning ? (
        <Alert variant="warning">
          {sendDisabledReason}{' '}
          <Button
            variant="link"
            className="p-0 align-baseline"
            onClick={() => setShowGlobalContactModal(true)}
          >
            Configure lab contact
          </Button>
          {' or '}
          <Button
            variant="link"
            className="p-0 align-baseline"
            onClick={() => handleLabContactModeChange(LAB_CONTACT_MODE.ONE_TIME)}
          >
            use a one-time lab contact
          </Button>
        </Alert>
      ) : null}

      <Card className="mb-4">
        <Card.Body>
          <AdHocLinesEditor
            lines={lines}
            onLinesChange={setLines}
            supplierSelectList={supplierSelectList}
            isLoadingSuppliers={isLoadingSuppliers}
            showStatus={false}
            sectionTitle="Custom lines"
            sectionDescription="Add one or more lines to request quotes from suppliers. Each line needs a name and supplier."
          />
        </Card.Body>
      </Card>

      {previewLines.length > 0 ? (
        <Card className="mb-4">
          <Card.Header>Supplier preview</Card.Header>
          <Card.Body>
            {supplierGroups.map(group => (
              <div
                key={group.supplierKey}
                className="border rounded p-3 mb-2"
                style={{ backgroundColor: '#f8f9fa' }}
              >
                <Badge bg="secondary" className="me-2">
                  {group.supplierName}
                </Badge>
                <span className="text-muted">
                  {group.lines.length} line{group.lines.length !== 1 ? 's' : ''}
                </span>
                <ul className="mb-0 mt-2 small">
                  {group.lines.map((line, idx) => (
                    <li key={idx}>
                      {line.line_name}
                      {line.line_cat_num ? ` (${line.line_cat_num})` : ''} — qty{' '}
                      {line.quantity}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </Card.Body>
        </Card>
      ) : null}

      {submitErrors.length > 0 ? (
        <Alert variant="danger">{submitErrors[0]}</Alert>
      ) : null}

      <CatalogQuoteEmailResultSummary result={successResult} />

      <div className="d-flex flex-wrap gap-2">
        <Button
          variant="outline-warning"
          disabled={!canSend || isSubmitting || previewLines.length === 0}
          title={
            sendDisabledReason ||
            'Send custom quote request emails for all lines above'
          }
          onClick={handleSend}
          className="d-inline-flex align-items-center"
        >
          {isSubmitting ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Sending…
            </>
          ) : (
            <>
              <PencilSquare className="me-2" aria-hidden />
              Send Custom Quote Request Email
            </>
          )}
        </Button>
        {successResult ? (
          <Button variant="outline-secondary" onClick={handleClearAfterSuccess}>
            Clear and add new lines
          </Button>
        ) : null}
      </div>

      <GlobalOrderContactModal
        open={showGlobalContactModal}
        onClose={() => setShowGlobalContactModal(false)}
        token={token}
        onSettingsUpdated={settings => {
          setGlobalContactSettings(settings);
          setGlobalContactLoadError('');
          setShowGlobalContactModal(false);
          setLabContactMode(
            defaultLabContactMode(
              !!(settings?.contact_name && settings?.contact_email)
            )
          );
          showToast('Lab contact saved successfully', 'success', 'top-right', 3000);
        }}
      />
    </div>
    </GlassWorkspaceShell>
  );
};

export default CustomQuoteRequestPage;
